import { Typography, Pagination, Box } from '@mui/material';
import SkeletonRow from './SkeletonRow';
import { formatCurrency, formatDateWithMoment } from '../../pages/apps/billing/utilities/helpers/helpers';

type InvoiceItem = {
	invoice_id: string;
	invoice_number: string;
	amount_paid: number;
	status: string;
	currency: string;
	invoice_created_date: string;
	invoice_pdf: string;
	plan_name: string;
};

type PaginationType = {
	per_page: number;
	total_returned: number;
	has_more: boolean;
	next_cursor?: string | null;
};

interface InvoicesTableProps {
	title?: string;
	invoices: InvoiceItem[];
	isLoading: boolean;
	isFetching?: boolean;
	pagination?: PaginationType;
	page?: number;
	pageCount?: number;
	onPageChange?: (page: number) => void;
}

export default function InvoicesTable({
	title = "All Invoices:",
	invoices,
	isLoading,
	isFetching = false,
	pagination,
	page = 1,
	pageCount = 1,
	onPageChange
}: InvoicesTableProps) {
	const showSkeleton = isLoading || isFetching;

	return (
		<div>
			<Typography
				variant="h6"
				className="text-2xl text-gray-900 capitalize mb-4"
				fontWeight="700"
			>
				{title}
			</Typography>
			{!showSkeleton && invoices.length === 0 ? (
				<div className="bg-white rounded-lg shadow-sm p-12 text-center">
					<Typography
						variant="body1"
						className="text-gray-600"
					>
						No invoices found.
					</Typography>
				</div>
			) : (
				<div className="bg-white rounded-lg shadow-sm overflow-hidden">
					<div className="overflow-x-auto">
						<table className="w-full">
							<thead className="bg-gray-50 border-b border-gray-200">
								<tr>
									<th className="px-6 py-4 text-left font-semibold text-gray-800">
										Invoice
									</th>
									<th className="px-6 py-4 text-left font-semibold text-gray-800">
										Plan
									</th>
									<th className="px-6 py-4 text-left font-semibold text-gray-800">
										Date
									</th>
									<th className="px-6 py-4 text-left font-semibold text-gray-800">
										Status
									</th>
									<th className="px-6 py-4 text-left font-semibold text-gray-800">
										Amount
									</th>
									<th className="px-6 py-4 text-left font-semibold text-gray-800">
										Receipt
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{showSkeleton
									? Array.from({ length: 5 }).map((_, i) => (
											<SkeletonRow key={i} columns={6} />
									  ))
									: invoices.map((invoice: InvoiceItem) => (
									<tr
										key={invoice.invoice_id}
										className="hover:bg-gray-50 transition-colors"
									>
										<td className="px-6 py-4 whitespace-nowrap">
											<Typography
												variant="body2"
												className="text-gray-900"
											>
												{invoice.invoice_number}
											</Typography>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<Typography
												variant="body2"
												className="text-gray-700 capitalize"
											>
												{invoice.plan_name}
											</Typography>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<Typography
												variant="body2"
												className="text-gray-700"
											>
												{formatDateWithMoment(invoice.invoice_created_date)}
											</Typography>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<span
												className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold capitalize ${
													invoice.status === 'paid'
														? 'bg-green-100 text-green-800'
														: 'bg-gray-100 text-gray-600'
												}`}
											>
												{invoice.status}
											</span>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<Typography
												variant="body2"
												className="text-gray-700"
											>
												{formatCurrency(
													invoice.currency || 'usd',
													invoice.amount_paid || 0
												)}
											</Typography>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<a
												role="button"
												tabIndex={0}
												onClick={() =>
													window.open(invoice.invoice_pdf, '_blank')
												}
												className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
												style={{ textDecoration: 'underline' }}
											>
												Download PDF
											</a>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					{/* Pagination */}
					{!showSkeleton && pagination && onPageChange && pageCount > 1 && (
						<Box
							className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-wrap gap-3"
							sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
						>
							<Typography variant="body2" className="text-gray-600">
								Showing page {page} ({pagination.total_returned} invoice
								{pagination.total_returned !== 1 ? 's' : ''})
							</Typography>
							<Pagination
								count={pageCount}
								page={page}
								onChange={(_, value) => onPageChange(value)}
								color="primary"
								shape="rounded"
								showFirstButton
								showLastButton
							/>
						</Box>
					)}
				</div>
			)}
		</div>
	);
}